/**
 * AddPatient 组件 - 同构实现
 * 使用 hy-ui-components 的 List、Input、Select、DatePicker，单一文件支持 H5 + 小程序
 */
import classnames from 'classnames';
import React, { forwardRef, useImperativeHandle } from 'react';

import { DatePicker } from '../DatePicker';
import { Input } from '../Input';
import { List } from '../List';
import { Select } from '../Select';
import { DEFAULT_CERT_TYPE, DEFAULT_GENDER, DEFAULT_PATIENT_RELATION } from './constants';
import { formatDate } from './format';
import { transformProvinceToNodes } from './province';
import type { EditPatientData, OptionItem, ProvinceNode } from './types';
import { useAddPatientForm } from './useAddPatientForm';

import './index.scss';

const PROVINCE_URL = 'https://static.za-doctor.com/common/province/area.json';
let provinceCache: ProvinceNode[] | null = null;

function defaultGetProvinceData(): Promise<ProvinceNode[]> {
  if (provinceCache) return Promise.resolve(provinceCache);
  const Taro = typeof window !== 'undefined' ? (window as any).Taro : null;
  if (Taro?.request) {
    return new Promise((resolve) => {
      Taro.request({
        url: `${PROVINCE_URL}?t=${Date.now()}`,
        method: 'GET',
        success: (res: any) => {
          const data = res?.data ?? res ?? [];
          provinceCache = Array.isArray(data) ? transformProvinceToNodes(data) : [];
          resolve(provinceCache);
        },
        fail: () => resolve([]),
      });
    });
  }
  return fetch(`${PROVINCE_URL}?t=${Date.now()}`)
    .then((res) => (res.ok ? res.json() : []))
    .then((data) => {
      provinceCache = transformProvinceToNodes(data);
      return provinceCache;
    });
}

export type { EditPatientData, OptionItem, ProvinceNode } from './types';
export type { PatientFormData } from './types';

export interface AddPatientProps {
  value?: EditPatientData;
  onChange?: (key: string, value: any, args?: any) => void;
  onSave?: (onSuccess?: () => void) => void;
  onSaveRequest?: (formData: EditPatientData, onSuccess?: () => void) => void | Promise<void>;
  showPatientDistrict?: boolean;
  patientRelationOptions?: OptionItem[];
  certTypeOptions?: OptionItem[];
  genderOptions?: OptionItem[];
  provinceData?: ProvinceNode[];
  onFetchHasSelf?: () => Promise<boolean>;
  onFetchPatientRelatives?: (patientName: string) => Promise<{ relativesCertNo?: string; relativesId?: string }>;
  onGetProvinceData?: () => Promise<ProvinceNode[]>;
  /** 请求：获取患者编号 patientNo，调用方需提供此函数 */
  onFetchPatientNo?: () => Promise<string>;
  onInitClear?: () => void;
  onInitDefaults?: () => void;
}

export interface AddPatientRef {
  savePatient: (callback?: () => void) => void;
}

const AddPatient = forwardRef<AddPatientRef, AddPatientProps>((props, ref) => {
  const form = useAddPatientForm({
    ...props,
    patientRelationOptions: props.patientRelationOptions ?? DEFAULT_PATIENT_RELATION,
    certTypeOptions: props.certTypeOptions ?? DEFAULT_CERT_TYPE,
    genderOptions: props.genderOptions ?? DEFAULT_GENDER,
    onGetProvinceData:
      props.onGetProvinceData ?? (props.showPatientDistrict !== false ? defaultGetProvinceData : undefined),
  });

  useImperativeHandle(ref, () => ({
    savePatient: form.savePatientClick,
  }));

  const prefixCls = 'addPatient-comp';

  return (
    <div className={prefixCls}>
      <div className={`card_component ${prefixCls}__card`}>
        <div className='headers'>
          <h4 className='headers__title'>添加成员</h4>
          <p className='headers__extra'>必填</p>
        </div>

        <List>
          <List.Item className={`select special ${prefixCls}__cell`} title='关系' hasArrow={false}>
            <div className='hy-list__cell-relation'>
              {form.patientRelationOptions.map((k, i) => (
                <div
                  role='button'
                  tabIndex={0}
                  onClick={() => {
                    if (!form.selfDisabled && (form.hasSelf ? k.value !== '1' : true)) {
                      form.patientInfoChange('patientRelation', k.value);
                    }
                  }}
                  onKeyDown={(e) => e.key === 'Enter' && (e.target as HTMLElement).click()}
                  className={classnames('button', {
                    'not-selected':
                      form.patientRelation !== k.value && String(form.patientRelation) !== String(k.value),
                    disabled: (form.hasSelf && (k.value === '1' || k.value === 1)) || form.selfDisabled,
                  })}
                  key={`rel-${i}-${k.value}`}
                >
                  {k.label}
                </div>
              ))}
            </div>
          </List.Item>

          <List.Item className={`input ${prefixCls}__cell`} title='姓名' hasArrow={false}>
            <Input
              placeholder='请输入真实姓名'
              value={form.patientName}
              onChange={(val) => form.patientInfoChange('patientName', val)}
              maxLength={15}
              onBlur={form.nameBlur}
              className={`${prefixCls}__input`}
            />
          </List.Item>

          <List.Item className={`input ${prefixCls}__cell`} title='证件类型' hasArrow>
            <Select
              dataSource={form.certTypeOptions}
              value={form.patientCertType}
              placeholder='应监管要求录入'
              onOk={(selected) => {
                form.patientInfoChange('patientCertType', (selected[0] || {}).value);
              }}
              className={`${prefixCls}__select`}
            />
          </List.Item>

          <List.Item className={`input ${prefixCls}__cell`} title='证件号码' hasArrow={false}>
            <Input
              placeholder={
                form.patientCertType === 'Q'
                  ? '婴幼儿可填写出生日期，格式19990101'
                  : '应监管要求录入证件号码'
              }
              value={form.patientCertNo}
              onChange={(val) => form.patientInfoChange('patientCertNo', val)}
              maxLength={form.patientCertType === 'I' ? 18 : 30}
              className={`${prefixCls}__input`}
            />
          </List.Item>

          {form.patientCertType && form.patientCertType !== 'I' && (
            <>
              <List.Item className={`input ${prefixCls}__cell`} title='性别' hasArrow={false}>
                <div className='hy-list__cell-relation'>
                  {form.genderOptions.map((k, i) => (
                    <div
                      role='button'
                      tabIndex={0}
                      onClick={() => form.patientInfoChange('patientGender', k.value)}
                      onKeyDown={(e) => e.key === 'Enter' && (e.target as HTMLElement).click()}
                      className={classnames('gender button', {
                        'not-selected':
                          form.patientGender !== k.value && String(form.patientGender) !== String(k.value),
                        disabled: form.idNumberDisabled,
                      })}
                      key={`gender-${i}-${k.value}`}
                    >
                      {k.label}
                    </div>
                  ))}
                </div>
              </List.Item>

              <List.Item className={`input ${prefixCls}__cell`} title='出生日期' hasArrow>
                <DatePicker
                  value={form.patientBirthday}
                  max={new Date()}
                  title='出生日期'
                  onOk={(dateVal) => form.patientInfoChange('patientBirthday', formatDate(dateVal, 'yyyy-MM-dd'))}
                  className={`${prefixCls}__select`}
                />
              </List.Item>
            </>
          )}

          <List.Item className={`input ${prefixCls}__cell`} title='体重(kg）' hasArrow={false}>
            <Input
              placeholder='12岁以下青少年需要录入此字段'
              value={form.patientWeight}
              onChange={(v) => form.changePatientWeight(v)}
              maxLength={6}
              className={`${prefixCls}__input`}
            />
          </List.Item>
        </List>
      </div>

      {form.showPatientDistrict && (
        <div className={`card_component ${prefixCls}__card`}>
          <List>
            <List.Item className={`select ${prefixCls}__cell`} title='所在地区' hasArrow>
              <Select
                dataSource={form.provinceData}
                value={form.districtValue}
                placeholder='应监管要求录入'
                onOk={(selected) => {
                  form.patientInfoChange(
                    'patientDistrictCode',
                    selected?.map((item) => item.value) ?? [],
                    selected?.map((item) => item.label).join('-')
                  );
                }}
                className={`${prefixCls}__select`}
              />
            </List.Item>
          </List>
        </div>
      )}
    </div>
  );
});

AddPatient.displayName = 'AddPatient';

export { AddPatient };
export default AddPatient;
